import React, { useEffect, useState } from 'react';
import { useAppDispatch, useAppSelector } from 'src/stores/slices/hooks';
import {
  Box,
  Button,
  Modal,
  SpaceBetween,
  StatusIndicator,
} from '@amzn/awsui-components-console';
import { useTranslation } from 'react-i18next';
import {
  selectShowUnlinkDevices,
  setShowUnlinkDevices,
} from 'src/stores/slices/userSlice';
import { useLinkedDevices } from 'src/services/queries/useLinkedDevices';
import DeviceList from 'src/components/linkedDevices/DeviceList';
import { LinkedDevice } from 'src/API';
import {
  Resource,
  ResourceAction,
  useHasResourcePermission,
} from 'src/hooks/useHasResourcePermission';
import { useSitesWithCodes } from 'src/hooks/useSitesWithCodes';
import { setUnlinkErrorText } from 'src/stores/slices/isc/devicesSlice';
import { unlinkDevices } from './actions/thunks';
import { prepareSingleQuote } from 'src/utils/helpers';
import { testIds } from '@features/isc/constants';

export default function UnlinkDevices(): React.ReactElement {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const showUnlinkDevices = useAppSelector(selectShowUnlinkDevices);
  const { sitesWithCodes } = useSitesWithCodes();

  const [selectedDevices, setSelectedDevices] = useState<LinkedDevice[]>([]);
  const [unlinkRequested, setUnlinkRequested] = useState(false);
  const { data: linkedDevices, isLoading } = useLinkedDevices();

  const { hasPermission: canUnlinkDevices } = useHasResourcePermission(
    Resource.DeviceLink,
    ResourceAction.Write
  );
  const unlinkingDevicesStatus = useAppSelector(
    state => state.deviceState.unlinkDevices
  );
  const [inProgress, setInProgress] = useState(false);

  const closeModal = (): void => {
    if (inProgress) return;

    dispatch(setShowUnlinkDevices(false));
    setSelectedDevices([]);
    setUnlinkRequested(false);
  };

  const mapRegionToDevice = (devices: LinkedDevice[]) => {
    return devices
      .map(device => {
        const regionName = device.deviceName.split('-')[0];
        const foundSite = sitesWithCodes.find(site => site.site === regionName);

        if (foundSite && foundSite.region) {
          return {
            deviceName: prepareSingleQuote(device.deviceName),
            cameraName: prepareSingleQuote(device.cameraName),
            region: foundSite?.region,
          };
        }
        dispatch(
          setUnlinkErrorText({
            deviceName: device.deviceName,
            errorText: 'Failed to find the device and region',
          })
        );
      })
      .filter(Boolean) as {
      cameraName: string;
      deviceName: string;
      region: number;
    }[];
  };

  const acceptHandler = () => {
    const devicesToUnlink = mapRegionToDevice(selectedDevices);

    dispatch(unlinkDevices(devicesToUnlink));
    setUnlinkRequested(true);
  };

  useEffect(() => {
    const statuses = Object.values(unlinkingDevicesStatus).map(device => {
      return device.loading;
    });
    setInProgress(statuses.includes(true));
  }, [unlinkingDevicesStatus]);

  return (
    <Modal
      visible={showUnlinkDevices}
      onDismiss={closeModal}
      closeAriaLabel="close unlink devices window"
      header="Unlink Device(s)"
      size="max"
      footer={
        <Box float="right">
          <SpaceBetween size="m" direction="horizontal">
            {!unlinkRequested && (
              <Button
                onClick={acceptHandler}
                disabled={!canUnlinkDevices || selectedDevices.length <= 0}
                data-testid={testIds.UNLINK_MODAL_ACCEPT_BUTTON}>
                Accept/Continue
              </Button>
            )}
            <Button onClick={closeModal} disabled={inProgress}>
              Close
            </Button>
          </SpaceBetween>
        </Box>
      }>
      {!linkedDevices?.length && !isLoading && (
        <p style={{ textAlign: 'center' }}>No Linked Devices.</p>
      )}
      {isLoading && (
        <Box padding="l">
          <SpaceBetween alignItems="center" size="m">
            <StatusIndicator type="loading">
              Loading Linked Devices
            </StatusIndicator>
          </SpaceBetween>
        </Box>
      )}
      {!isLoading && !!linkedDevices?.length && (
        <>
          <p style={{ padding: '0 10px' }}>
            {t('Choose the device(s) to be unlinked.')}
          </p>
          <DeviceList
            selectionChangeHandler={deviceList => {
              setSelectedDevices(deviceList);
            }}
            selectedDevices={selectedDevices}
            selectionDisabled={!canUnlinkDevices || unlinkRequested}
            devices={linkedDevices || []}
          />
        </>
      )}
    </Modal>
  );
}
